import React from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Hyperlink,
  Dropdown,
  IconButton,
  ActionRow,
} from "@openedx/paragon";
import { MoreHoriz } from "@openedx/paragon/icons";
import { useIntl } from "@edx/frontend-platform/i18n";
import { getConfig } from "@edx/frontend-platform";
import { Link } from "react-router-dom";

import { COURSE_CREATOR_STATES } from "../../constants";
import { getStudioHomeData } from "../data/selectors";
import messages from "../messages";
import { trimSlashes } from "./utils";

interface BaseProps {
  displayName: string;
  org: string;
  number: string;
  run?: string;
  lmsLink?: string | null;
  rerunLink?: string | null;
  courseKey?: string;
  isLibraries?: boolean;
  isPaginated?: boolean;
  showDeleteOption?: boolean;
  handleDelete?: (courseId, courseName) => void;
}
type Props = BaseProps &
  /** If we should open this course/library in this MFE, this is the path to the edit page, e.g. '/course/foo' */
  (| { path: string; url?: never }
    /**
     * If we might be redirecting to the legacy Studio view, this is the URL to redirect to.
     * URLs starting with '/' are assumed to be relative to the legacy Studio root.
     */
    | { url: string; path?: never }
  );

/**
 * A card on the Studio home page that represents a Course or a Library
 */
const CardItem: React.FC<Props> = ({
  displayName,
  lmsLink = "",
  rerunLink = "",
  org,
  number,
  run = "",
  isLibraries = false,
  courseKey = "",
  isPaginated = false,
  path,
  url,
  showDeleteOption = false,
  handleDelete,
}) => {
  const intl = useIntl();
  const { allowCourseReruns, courseCreatorStatus, rerunCreatorStatus } =
    useSelector(getStudioHomeData);
  const destinationUrl: string =
    path ?? new URL(url, getConfig().STUDIO_BASE_URL).toString();
  const subtitle = isLibraries
    ? `${org} / ${number}`
    : `${org} / ${number} / ${run}`;
  const readOnlyItem = !(lmsLink || rerunLink || url || path);
  const showActions = !(readOnlyItem || isLibraries);
  const isShowRerunLink =
    allowCourseReruns &&
    rerunCreatorStatus &&
    courseCreatorStatus === COURSE_CREATOR_STATES.granted;
  const hasDisplayName = (displayName ?? "").trim().length
    ? displayName
    : courseKey;

  return (
    <Card className="card-item">
      <Card.Header
        size="sm"
        title={
          !readOnlyItem ? (
            <Link className="card-item-title" to={destinationUrl}>
              {hasDisplayName}
            </Link>
          ) : (
            <span className="card-item-title">{displayName}</span>
          )
        }
        subtitle={subtitle}
        actions={
          showActions &&
          (isPaginated ? (
            <Dropdown>
              <Dropdown.Toggle
                as={IconButton}
                iconAs={MoreHoriz}
                variant="primary"
                data-testid="toggle-dropdown"
              />
              <Dropdown.Menu>
                {isShowRerunLink && (
                  <Dropdown.Item href={trimSlashes(rerunLink ?? "")}>
                    {messages.btnReRunText.defaultMessage}
                  </Dropdown.Item>
                )}
                <Dropdown.Item href={lmsLink}>
                  {intl.formatMessage(messages.viewLiveBtnText)}
                </Dropdown.Item>
                {showDeleteOption && (
                  <Dropdown.Item
                    onClick={() => handleDelete && handleDelete(courseKey, displayName)}
                  >
                    {intl.formatMessage(messages.deleteCourseButtonText)}
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <ActionRow>
              {isShowRerunLink && (
                <Hyperlink
                  className="small"
                  destination={trimSlashes(rerunLink ?? "")}
                  key={`action-row-rerunLink-${courseKey}`}
                >
                  {intl.formatMessage(messages.btnReRunText)}
                </Hyperlink>
              )}
              <Hyperlink
                className="small ml-3"
                destination={lmsLink ?? ""}
                key={`action-row-lmsLink-${courseKey}`}
              >
                {intl.formatMessage(messages.viewLiveBtnText)}
              </Hyperlink>
            </ActionRow>
          ))
        }
      />
    </Card>
  );
};

export default CardItem;
